import { Color3, type Mesh, MeshBuilder, Quaternion, StandardMaterial, Vector3 } from "@babylonjs/core";
import { Btn, HeroId, Slot, fxk, hv, pk, zk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import { bell, fm, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import type { Cue } from "../../audio/engine.ts";
import type { ClientHero, FxCtx } from "../types.ts";

const V = { beamTarget: 0, chaos: 1, phase: 2, ultPulse: 3 } as const;
const PK = { Burst: pk(HeroId.ScarletTwitch, 0), Seal: pk(HeroId.ScarletTwitch, 1) } as const;
const ZK = { Seal: zk(HeroId.ScarletTwitch, 0) } as const;
const FX = { Chaos: fxk(HeroId.ScarletTwitch, 0), Burst: fxk(HeroId.ScarletTwitch, 1), Seal: fxk(HeroId.ScarletTwitch, 2), Erase: fxk(HeroId.ScarletTwitch, 3) } as const;
const hz = (s: number) => 293.66 * 2 ** (s / 12);
const chaosCue: Cue = trim(22.5, (o) => {
  tone(o, { type: "sawtooth", f: 720, f1: 1900, glide: 0.06, gain: 0.08, d: 0.09 });
  noise(o, { color: "crackle", type: "highpass", f: 2400, gain: 0.16, d: 0.1 });
  return 0.16;
});
const bigHex: Cue = trim(-3, (o) => {
  thump(o, 0.7, 74, 28, 0.55);
  whoosh(o, 0.55, 300, 3200, 0.7);
  fm(o, { f: 146, ratio: 1.5, index: 6, index1: 0.8, gain: 0.2, d: 1 });
  return 1.5;
});
const theme = {
  bpm: 132,
  octave: 0,
  voice: (o, f, d, v) => fm(o, { f, ratio: 2.01, index: 4, index1: 0, gain: v * 0.13, d }),
  double: (o, f, d, v) => bell(o, f * 3, d * 0.4, v * 0.3),
  notes: [
    [0, 0.5],
    [2, 0.25],
    [6, 0.25],
    [7, 1],
    [12, 1],
  ],
} satisfies ClientHero["audio"]["theme"];

type ScarletGear = { beam: Mesh; beam2: Mesh; beam3: Mesh; halo: Mesh; crown: Mesh; cape: Mesh };

const hero: ClientHero<ScarletGear> = {
  id: HeroId.ScarletTwitch,
  bulk: 0.95,
  shape: { head: 1.05, torso: 0.95, arms: 1.05 },
  gear(g) {
    const { R, H, scene, add, addWorld, rig, glow, toon } = g;
    const crown = add(MeshBuilder.CreateTorus("glitch-crown", { diameter: R * 1.45, thickness: 0.045, tessellation: 5 }, scene), rig.head, glow("#ff88c2"));
    crown.position.y = R * 0.78;
    crown.rotation.z = 0.2;
    const cape = add(MeshBuilder.CreateBox("hex-cape", { width: R * 1.5, height: H * 0.42, depth: 0.04 }, scene), rig.torso, toon("#2b1039"));
    cape.position.set(0, -H * 0.08, R * 0.7);
    const coatTail = add(MeshBuilder.CreateBox("chaos-coat-tail", { width: R * 1.1, height: H * 0.45, depth: 0.045 }, scene), rig.torso, toon("#120515"));
    coatTail.position.set(0, -H * 0.2, R * 0.73);
    const brow = add(MeshBuilder.CreateBox("crimson-brow", { width: R * 1.3, height: 0.055, depth: 0.09 }, scene), rig.head, glow("#ff3b98"));
    brow.position.set(0, R * 0.62, -R * 0.55);
    for (const hand of [rig.handL, rig.handR]) {
      const orb = add(MeshBuilder.CreateSphere("hand-hex", { diameter: 0.22, segments: 8 }, scene), hand, glow("#ff55b7"));
      orb.position.z = -0.28;
    }
    const beam = addWorld(MeshBuilder.CreateCylinder("chaos-beam", { diameter: 0.12, height: 1, tessellation: 8 }, scene), glow("#ff55b7", 0.85));
    beam.setEnabled(false);
    const beam2 = addWorld(MeshBuilder.CreateCylinder("chaos-beam-fork", { diameter: 0.045, height: 1, tessellation: 6 }, scene), glow("#ffd6e9", 0.75));
    const beam3 = addWorld(MeshBuilder.CreateCylinder("chaos-beam-fork", { diameter: 0.04, height: 1, tessellation: 6 }, scene), glow("#ff007f", 0.7));
    beam2.setEnabled(false);
    beam3.setEnabled(false);
    const halo = addWorld(MeshBuilder.CreateTorus("erase-halo", { diameter: 2, thickness: 0.06, tessellation: 48 }, scene), glow("#ff007f", 0.35));
    halo.setEnabled(false);
    return { beam, beam2, beam3, halo, crown, cape };
  },
  animate(gear, f, rig) {
    const ref = f.v[V.beamTarget] > 0 ? f.lookup(Math.round(f.v[V.beamTarget])) : undefined;
    gear.beam.setEnabled(!!ref);
    gear.beam2.setEnabled(!!ref);
    gear.beam3.setEnabled(!!ref);
    if (ref) {
      const from = new Vector3(f.eye.x, f.eye.y - 0.12, f.eye.z);
      const to = new Vector3(ref.center.x, ref.center.y, ref.center.z);
      orient(gear.beam, from, to);
      const jitterA = new Vector3(Math.sin(f.t * 37) * 0.1, Math.cos(f.t * 31) * 0.08, Math.sin(f.t * 23) * 0.1);
      const jitterB = new Vector3(Math.cos(f.t * 29) * 0.12, Math.sin(f.t * 41) * 0.07, Math.cos(f.t * 19) * 0.1);
      orient(gear.beam2, from.add(jitterA.scale(0.4)), to.add(jitterA));
      orient(gear.beam3, from.add(jitterB.scale(0.4)), to.add(jitterB));
      gear.beam.scaling.x = gear.beam.scaling.z = 1 + Math.sin(f.t * 80) * 0.2;
    }
    gear.halo.setEnabled(f.ultActive);
    if (f.ultActive) {
      gear.halo.position.set(f.pos.x, f.pos.y + 0.12, f.pos.z);
      gear.halo.scaling.setAll(1 + (1 - f.v[V.ultPulse]) * 7);
      gear.halo.rotation.y += f.dt * 4;
    }
    gear.crown.rotation.y += f.dt * (f.v[V.phase] > 0 ? 10 : 2);
    gear.cape.rotation.x = 0.08 + (f.v[V.phase] > 0 ? 0.25 : 0) + Math.sin(f.t * 8) * 0.025;
    for (const p of Object.values(rig.parts)) p.visibility = f.v[V.phase] > 0 ? 0.45 : 1;
    return f.ultActive || f.v[V.phase] > 0 ? "holding-both" : undefined;
  },
  hud(c, eid) {
    return [
      { label: "Chaos", value: hv(c, eid, V.chaos) / 4, color: "#ff88c2" },
      { label: "Phase", value: hv(c, eid, V.phase), color: "#ffe0ef" },
    ];
  },
  lifting(c, eid, _buttons, _grounded) {
    return hv(c, eid, V.phase) > 0;
  },
  projectiles: {
    [PK.Burst]: (scene, f) => orb(scene, f.pos, "#ff55b7", 0.36),
    [PK.Seal]: (scene, f) => orb(scene, f.pos, "#ff88c2", 0.28),
  },
  zones: {
    [ZK.Seal]: (scene, f) => {
      const m = MeshBuilder.CreateTorus("lag-seal", { diameter: f.radius * 2, thickness: 0.08, tessellation: 36 }, scene);
      m.material = new StandardMaterial("lag-seal-mat", scene);
      (m.material as StandardMaterial).emissiveColor = Color3.FromHexString("#ff88c2");
      (m.material as StandardMaterial).alpha = 0.4;
      const cross = MeshBuilder.CreateTorus("lag-seal-inner", { diameter: f.radius * 1.1, thickness: 0.035, tessellation: 6 }, scene);
      cross.material = m.material;
      cross.parent = m;
      return {
        gear: m,
        update(_mesh, fr) {
          m.position.set(fr.pos.x, fr.pos.y + 0.07, fr.pos.z);
          m.rotation.y += fr.dt * 2;
          m.scaling.setAll(1 + Math.sin(fr.age * 6) * 0.04);
        },
        dispose: () => m.dispose(),
      };
    },
  },
  icons: {
    [Slot.Primary]: "M4 12 C8 6 16 6 20 12 M4 12 C8 18 16 18 20 12 M8 12 H16",
    [Slot.Secondary]: "M12 3 L15 9 L21 12 L15 15 L12 21 L9 15 L3 12 L9 9 Z",
    [Slot.Ability1]: "M5 18 C9 12 15 12 19 6 M8 6 H19 V17",
    [Slot.Ability2]: "M12 4 L20 8 V16 L12 20 L4 16 V8 Z M8 12 H16",
    [Slot.Ultimate]: "M4 12 C4 6 8 4 12 4 C16 4 20 6 20 12 C20 18 16 20 12 20 C8 20 4 18 4 12 M8 8 L16 16 M16 8 L8 16",
    passive: "M7 18 C10 13 14 11 17 6 M7 6 C10 11 14 13 17 18",
  },
  fx: {
    [FX.Burst]: { explosion: (c, pos, radius) => pulse(c, pos, radius, "#ff2f95") },
    [FX.Seal]: { explosion: (c, pos, radius) => pulse(c, pos, radius, "#ff88c2") },
    [FX.Erase]: { explosion: (c, pos, radius) => pulse(c, pos, radius, "#ff007f") },
  },
  audio: {
    weight: "light",
    fire: (ctx) => ({ key: ctx?.alt ? "scarlet-packet" : "scarlet-beam", cue: chaosCue }),
    ability: {
      [Slot.Secondary]: chaosCue,
      [Slot.Ability1]: chaosCue,
      [Slot.Ability2]: chaosCue,
      [Slot.Ultimate]: bigHex,
    },
    impacts: { [FX.Burst]: chaosCue, [FX.Seal]: chaosCue },
    explosions: { [FX.Burst]: () => bigHex, [FX.Seal]: () => chaosCue, [FX.Erase]: () => bigHex },
    flight: { [PK.Burst]: loops.flight("orb"), [PK.Seal]: loops.flight("orb") },
    zoneLoops: { [ZK.Seal]: { build: loops.portal, gain: 0.55, range: 28 } },
    loops: ({ v, ultActive, alive }) => (alive && (v[V.beamTarget] > 0 || ultActive) ? [{ key: "scarlet-chaos", build: ultActive ? loops.barrage : loops.visor, gain: ultActive ? 0.8 : 0.45 }] : []),
    selfLift: ({ alive, v, ultActive }) => (alive && (v[V.phase] > 0 || ultActive) ? loops.levitate : undefined),
    theme,
    themeTrim: { heroic: 2.5, hostile: -5.8, tease: 8 },
  },
};

function orient(m: { position: Vector3; scaling: Vector3; rotationQuaternion: Quaternion | null }, from: Vector3, to: Vector3) {
  const len = Vector3.Distance(from, to);
  m.position = Vector3.Lerp(from, to, 0.5);
  m.scaling.y = len;
  const dir = to.subtract(from).normalize();
  const axis = Vector3.Cross(Vector3.Up(), dir);
  const angle = Math.acos(Math.max(-1, Math.min(1, Vector3.Dot(Vector3.Up(), dir))));
  m.rotationQuaternion = axis.lengthSquared() > 1e-8 ? Quaternion.RotationAxis(axis.normalize(), angle) : null;
}

function orb(scene: Parameters<NonNullable<ClientHero["projectiles"]>[number]>[0], pos: { x: number; y: number; z: number }, color: string, size: number) {
  const m = MeshBuilder.CreateSphere("chaos-orb", { diameter: size, segments: 12 }, scene);
  m.material = new StandardMaterial("chaos-orb-mat", scene);
  (m.material as StandardMaterial).emissiveColor = Color3.FromHexString(color);
  const glyph = MeshBuilder.CreateTorus("chaos-orb-glyph", { diameter: size * 1.55, thickness: size * 0.07, tessellation: 6 }, scene);
  glyph.material = m.material;
  glyph.parent = m;
  return {
    gear: m,
    update(_mesh: Mesh, fr: Parameters<NonNullable<ClientHero["projectiles"]>[number]>[1]) {
      m.position.set(fr.pos.x, fr.pos.y, fr.pos.z);
      m.rotation.y += fr.dt * 12;
      glyph.rotation.x += fr.dt * 9;
    },
    dispose: () => m.dispose(),
  };
}

function pulse(c: FxCtx, pos: { x: number; y: number; z: number }, radius: number, color: string) {
  const m = MeshBuilder.CreateSphere("chaos-pulse", { diameter: 2, segments: 16 }, c.scene);
  m.material = c.glow(color, 0.45);
  m.position.set(pos.x, pos.y, pos.z);
  c.timed(m, 0.4, (t, mesh) => {
    mesh.scaling.setAll(radius * Math.sqrt(t));
    mesh.visibility = 1 - t;
  });
}

void Btn;
void hz;
export default hero;
